import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import messages from './data/ServiceMessages.json';
import versionHistories from './data/ServiceMessageVersions.json';
import { CATEGORY_META, SEVERITY_CLS, fmt, getSummary, getDetail, usePageMeta, ActionCountdownBadge, buildMessageSeoTitle, buildMessageSeoDescription } from './shared';
import { buildHtmlDiff, formatMessageBodyHtml, normalizeVersionHistory } from './messageVersions';
import { CompareToggle, ListDiff, VersionTimeline, WordDiff } from './MessageVersionHistory';
import Layout from './Layout';
import ShareButtons from './ShareButtons';
import SponsorBanner from './SponsorBanner';

// Lookup map for O(1) access by ID
const messageMap = Object.fromEntries(messages.map(m => [m.id, m]));

// ── MessageDetail page ────────────────────────────────────────────────────────

export default function MessageDetail() {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const latestMessage = messageMap[id];
  const history = useMemo(
    () => normalizeVersionHistory(versionHistories[id], latestMessage),
    [id, latestMessage]
  );
  const latest = history[history.length - 1];
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const message = history.find(entry => entry._version === selectedVersion) ?? latest;
  const messageIndex = history.indexOf(message);
  const previousMessage = messageIndex > 0 ? history[messageIndex - 1] : null;

  useEffect(() => {
    setSelectedVersion(latest?._version ?? null);
    setCompareMode(false);
  }, [id, latest?._version]);

  useEffect(() => {
    setCompareMode(false);
  }, [selectedVersion]);

  // 404 guard
  useEffect(() => {
    if (!latestMessage) navigate('/', { replace: true });
  }, [latestMessage, navigate]);

  const summary   = getSummary(message ?? {});
  const aiSummary = message?._aiSummary;
  const roadmap   = getDetail(message ?? {}, 'RoadmapIds');
  const platforms = getDetail(message ?? {}, 'Platforms');
  const catMeta   = message
    ? (CATEGORY_META[message.category] ?? { label: message.category, cls: 'bg-gray-100 text-gray-700 border-gray-200' })
    : null;

  // Related messages: same services or tags, ranked by overlap score
  const related = useMemo(() => {
    if (!message) return [];
    const svcSet = new Set(message.services ?? []);
    const tagSet = new Set(message.tags ?? []);
    return messages
      .filter(m => m.id !== message.id)
      .map(m => ({
        m,
        score: (m.services?.filter(s => svcSet.has(s)).length ?? 0) +
               (m.tags?.filter(t => tagSet.has(t)).length ?? 0),
      }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
      .map(({ m }) => m);
  }, [message]);

  // Per-page meta tags
  const pageUrl = `https://msmessagecenter.com/${message?.id ?? ''}`;
  const pageTitle = message
    ? buildMessageSeoTitle(message)
    : 'Microsoft Message Center';
  const pageDesc = message
    ? buildMessageSeoDescription(message, summary)
    : 'Microsoft 365 Message Center update. Track planned changes and new features.';
  usePageMeta({ title: pageTitle, description: pageDesc, url: pageUrl });

  if (!message) return null;

  const dates = [
    { label: 'Published',    value: fmt(message.startDateTime), previousValue: fmt(previousMessage?.startDateTime) },
    { label: 'Expires',      value: fmt(message.endDateTime), previousValue: fmt(previousMessage?.endDateTime) },
    { label: 'Last updated', value: fmt(message.lastModifiedDateTime), previousValue: fmt(previousMessage?.lastModifiedDateTime) },
    { label: 'Action by',    value: fmt(message.actionRequiredByDateTime), previousValue: fmt(previousMessage?.actionRequiredByDateTime), red: true },
    { label: 'Roadmap ID',   value: roadmap, previousValue: getDetail(previousMessage ?? {}, 'RoadmapIds') },
    { label: 'Platforms',    value: platforms, previousValue: getDetail(previousMessage ?? {}, 'Platforms') },
  ].filter(d => d.value);

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">

        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="text-xs text-gray-400 dark:text-gray-500 mb-5 flex items-center gap-1.5">
          <Link to="/" className="hover:text-blue-600 dark:hover:text-blue-400">All messages</Link>
          <span>/</span>
          <span className="text-gray-600 dark:text-gray-300 font-medium">{message.id}</span>
        </nav>

        <SponsorBanner message={message} />

        <div className={history.length > 1 ? 'grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-8 lg:gap-10' : ''}>
        <VersionTimeline history={history} selectedVersion={message._version} onSelect={setSelectedVersion} />
        <div className="min-w-0">
        <CompareToggle previousVersion={previousMessage?._version} checked={compareMode} onChange={setCompareMode} />
        {message._version !== latest._version && (
          <div className="bg-amber-50 text-amber-900 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-700 px-4 py-3 mb-6 text-sm">
            You are viewing v{message._version}.{' '}
            <button type="button" className="font-semibold underline" onClick={() => setSelectedVersion(latest._version)}>Switch to latest (v{latest._version})</button>
          </div>
        )}

        <article>
        {/* Badges */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-sm font-mono font-semibold text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2.5 py-1">
            {message.id}
          </span>
          <span className={`text-sm font-medium px-2.5 py-1 border ${catMeta.cls}`}>
            {catMeta.label}
          </span>
          {message.severity !== 'normal' && (
            <span className={`text-sm px-2.5 py-1 ${SEVERITY_CLS[message.severity] ?? ''}`}>
              {message.severity}
            </span>
          )}
          {message.isMajorChange && (
            <span className="text-sm font-semibold px-2.5 py-1 bg-purple-100 text-purple-800 border border-purple-200">
              ★ Major Change
            </span>
          )}
          {message.actionRequiredByDateTime && (
            <span className="text-sm font-semibold px-2.5 py-1 bg-red-600 text-white">
              Action required
            </span>
          )}
          {message.actionRequiredByDateTime && (
            <ActionCountdownBadge dateStr={message.actionRequiredByDateTime} size="lg" />
          )}
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-50 leading-tight mb-3">
          {message.id}:{' '}
          {compareMode && previousMessage?.title !== message.title
            ? <WordDiff current={message.title} previous={previousMessage?.title} />
            : message.title}
        </h1>

        {/* Share */}
        <div className="mb-5">
          <ShareButtons
            url={`https://msmessagecenter.com/${message.id}`}
            text={`${message.title} – via MS Message Center`}
          />
        </div>

        {/* AI editorial analysis */}
        {aiSummary && (
          <section className="rounded-lg border border-cyan-200 bg-cyan-50 px-5 py-4 mb-6 dark:border-cyan-800 dark:bg-cyan-950/40" aria-labelledby="ai-summary-heading">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <h2 id="ai-summary-heading" className="text-xs font-semibold text-cyan-800 dark:text-cyan-300 uppercase tracking-wide">MS Message Center's Summary</h2>
              <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-cyan-900 dark:bg-gray-900 dark:text-cyan-200">
                Importance {aiSummary.importanceScore}/10
              </span>
            </div>
            <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed mb-4">{aiSummary.summary}</p>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="font-semibold text-gray-900 dark:text-gray-100">Administrator impact</dt>
                <dd className="text-gray-700 dark:text-gray-300">{aiSummary.administratorImpact}</dd>
              </div>
              <div>
                <dt className="font-semibold text-gray-900 dark:text-gray-100">End user impact</dt>
                <dd className="text-gray-700 dark:text-gray-300">{aiSummary.endUserImpact}</dd>
              </div>
            </dl>
            <p className="mt-3 text-xs text-gray-600 dark:text-gray-400">
              <span className="font-semibold">Score rationale:</span> {aiSummary.importanceReason}
            </p>
            {(aiSummary.learnLinks?.length ?? 0) > 0 && (
              <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                {aiSummary.learnLinks.map(link => (
                  <li key={link.url}>
                    <a href={link.url} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-700 hover:underline dark:text-blue-300">{link.title}</a>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* Summary */}
        {summary && (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-hard px-5 py-4 mb-6">
            <p className="text-xs font-semibold text-blue-500 dark:text-blue-400 uppercase tracking-wide mb-1">Microsoft Summary</p>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              {compareMode && getSummary(previousMessage ?? {}) !== summary
                ? <WordDiff current={summary} previous={getSummary(previousMessage ?? {}) ?? ''} />
                : summary}
            </p>
          </div>
        )}

        {/* Meta grid */}
        {dates.length > 0 && (
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            {dates.map(({ label, value, previousValue, red }) => (
              <div key={label} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-4 py-3">
                <dt className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">{label}</dt>
                <dd className={`text-sm font-semibold ${red ? 'text-red-600' : 'text-gray-800 dark:text-gray-100'}`}>
                  {compareMode && previousValue !== value ? (
                    <><span className="text-red-600 line-through">{previousValue ?? 'Not set'}</span><span className="mx-2 text-gray-400">→</span><span className="text-green-700 dark:text-green-400">{value}</span></>
                  ) : value}
                </dd>
              </div>
            ))}
          </dl>
        )}

        {/* Services + tags */}
        {((message.services?.length ?? 0) + (message.tags?.length ?? 0)) > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {compareMode ? (
              <ListDiff current={[...(message.services ?? []), ...(message.tags ?? [])]} previous={[...(previousMessage?.services ?? []), ...(previousMessage?.tags ?? [])]} />
            ) : <>
            {message.services?.map(svc => (
              <span key={svc} className="text-sm bg-sky-50 text-sky-700 border border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-700 px-3 py-1 rounded-full">
                {svc}
              </span>
            ))}
            {message.tags?.map(tag => (
              <span key={tag} className="text-sm bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 px-3 py-1 rounded-full">
                {tag}
              </span>
            ))}
            </>}
          </div>
        )}

        {/* Full HTML body */}
        {message.body?.content && (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-hard px-6 py-6">
            {compareMode && previousMessage?.body?.content !== message.body.content ? (
              <div className="message-body text-sm text-gray-700 dark:text-gray-300 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: buildHtmlDiff(
                  formatMessageBodyHtml(message.body.content),
                  formatMessageBodyHtml(previousMessage?.body?.content)
                ) }} />
            ) : (
              <div className="message-body text-sm text-gray-700 dark:text-gray-300 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: formatMessageBodyHtml(message.body.content) }} />
            )}
          </div>
        )}

        {/* Related messages */}
        {related.length > 0 && (
          <section className="mt-10" aria-label="Related messages">
            <h2 className="text-base font-semibold text-gray-700 dark:text-gray-300 mb-4">Related Messages</h2>
            <ul className="space-y-2">
              {related.map(m => {
                const cm = CATEGORY_META[m.category] ?? { label: m.category, cls: 'bg-gray-100 text-gray-700 border-gray-200' };
                return (
                  <li key={m.id}>
                    <Link
                      to={`/${m.id}`}
                    className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors group"
                    >
                      <span className="text-xs font-mono font-semibold text-gray-400 bg-gray-100 dark:bg-gray-800 dark:text-gray-500 px-2 py-0.5 shrink-0">{m.id}</span>
                      <span className={`text-xs font-medium px-2 py-0.5 border shrink-0 ${cm.cls}`}>{cm.label}</span>
                      <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 dark:text-gray-100 truncate group-hover:text-blue-700 dark:group-hover:text-blue-400 transition-colors">{m.title}</span>
                      <svg className="w-4 h-4 text-gray-300 group-hover:text-blue-400 shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {/* Back link */}
        <div className="mt-8">
          <Link to="/"
            className="inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to all messages
          </Link>
        </div>
        </article>
        </div>
        </div>
      </div>
    </Layout>
  );
}
